<template>
  <div class="advanced-filters-section">
    <div v-if="expanded">
      <button v-if="!alwaysExpanded" type="button" class="toggle-button btn btn-sm btn-link" @click="expanded = false">
        {{ i18n.t('public.local.advanced_filters_hide') }}
      </button>
      <div v-if="showIncludeVirtualFilter">
        <include-virtual-filter :initial-value="initialValues.include_virtual_events"
                                @change="(value) => updateKey('IncludeVirtual', { value })" />
      </div>
      <div v-if="showUpcomingOnlyFilter">
        <toggle :label="i18n.t('calendars.event_search_form.past_events')"
                name="include_past"
                :initial-value="!initialValues.upcoming_only" @change="({ value }) => updateKey('UpcomingOnly', {value: !value})" />
      </div>
      <div class="mt10 d-flex align-items-center flex-wrap flex-md-nowrap">
        <label class="me-2">
          {{ i18n.t('activerecord.attributes.event.start_at') }}
        </label>
        <start-date-filter :initial-range="initialValues.start_date_range"
                           @change="(value) => updateKey('StartDate', { value })" />
      </div>
      <div v-if="showEventTypesFilter" class="mt10">
        <multi-select-filter ref="event-type"
                             :label="i18n.t('activerecord.models.event_type.other')"
                             :options="eventTypeOptions" :initial-values="initialValues.event_types"
                             :selected-value-display-variety="selectedValueDisplayVariety"
                             @change="(value) => updateKey('EventType', { value })"
                             @show-chooser="closeFiltersExceptOne('event-type')" />
      </div>
      <div v-if="showRegionsFilter" class="mt10">
        <multi-select-filter ref="region"
                             :label="i18n.t('activerecord.models.region.other')"
                             :options="regionOptions" :initial-values="initialValues.regions"
                             :selected-value-display-variety="selectedValueDisplayVariety"
                             @change="(value) => updateKey('Region', { value })"
                             @show-chooser="closeFiltersExceptOne('region')" />
      </div>
      <div v-if="showLabelsFilter" class="mt10">
        <multi-select-filter ref="label"
                             :label="i18n.t('activerecord.models.label.other')"
                             :options="labelOptions" :initial-values="initialValues.labels"
                             :selected-value-display-variety="selectedValueDisplayVariety"
                             @change="(value) => updateKey('Label', { value })"
                             @show-chooser="closeFiltersExceptOne('label')" />
      </div>
    </div>
    <div v-else>
      <button v-if="!alwaysExpanded" type="button" class="toggle-button btn btn-sm btn-link" @click="expanded = true">
        {{ i18n.t('public.local.advanced_filters_show') }}
      </button>
    </div>
  </div>
</template>

<script>
import {FILTER_KEY_EVENT_TYPES, FILTER_KEY_INCLUDE_VIRTUAL, FILTER_KEY_LABELS,
  FILTER_KEY_REGIONS, FILTER_KEY_START_DATE_RANGE, FILTER_KEY_UPCOMING_ONLY} from './filter-keys'
import IncludeVirtualFilter from './include-virtual-filter'
import { mapState } from 'vuex'
import MultiSelectFilter from './multi-select-filter'
import Toggle from '../components/inputs/toggle'
import StartDateFilter from './start-date-filter'

const DEFAULT_VALUES = {
  [FILTER_KEY_EVENT_TYPES]: [],
  [FILTER_KEY_INCLUDE_VIRTUAL]: 'all',
  [FILTER_KEY_LABELS]: [],
  [FILTER_KEY_REGIONS]: [],
  [FILTER_KEY_START_DATE_RANGE]: null,
  [FILTER_KEY_UPCOMING_ONLY]: true
}

export default {
  components: { StartDateFilter, IncludeVirtualFilter, MultiSelectFilter, Toggle },

  props: {
    allowedFilterKeys: {
      type: Array,
      required: true
    },
    alwaysExpanded: {
      type: Boolean,
      required: false
    },
    selectedValueDisplayVariety: {
      type: String,
      required: false,
      default: 'accent'
    }
  },

  emits: ['change'],

  data() {
    return {
      expanded: this.alwaysExpanded
    }
  },

  computed: {
    ...mapState(['filters', 'filterOptions']),

    defaultExpanded() {
      // By default, show expanded if any filter is set to other than the default setting
      // defined above
      for (const filterKey of this.allowedFilterKeys) {
        if (this.initialValues[filterKey] !== DEFAULT_VALUES[filterKey]) {
          return true
        }
      }
      return false
    },

    eventTypeOptions() {
      return this.filterOptions[FILTER_KEY_EVENT_TYPES]
    },

    labelOptions() {
      return this.filterOptions[FILTER_KEY_LABELS]
    },

    initialValues() {
      const parsedInitialValues = {}

      for (const filterKey of this.allowedFilterKeys) {
        const filter = this.filters.find((f) => f.type === filterKey)
        parsedInitialValues[filterKey] = filter ? filter.value : DEFAULT_VALUES[filterKey]
      }

      return parsedInitialValues
    },

    regionOptions() {
      return this.filterOptions[FILTER_KEY_REGIONS]
    },

    showEventTypesFilter() {
      return this.shouldShowFilter(FILTER_KEY_EVENT_TYPES)
    },

    showIncludeVirtualFilter() {
      return this.shouldShowFilter(FILTER_KEY_INCLUDE_VIRTUAL)
    },

    showLabelsFilter() {
      return this.shouldShowFilter(FILTER_KEY_LABELS)
    },

    showRegionsFilter() {
      return this.shouldShowFilter(FILTER_KEY_REGIONS)
    },

    showUpcomingOnlyFilter() {
      return this.shouldShowFilter(FILTER_KEY_UPCOMING_ONLY)
    }
  },

  created() {
    // Q: Why can't we just do this in data()?
    // A: Because computed properties aren't available when data() is evaluated.
    this.expanded = this.expanded || this.defaultExpanded
  },

  methods: {
    closeFiltersExceptOne(filterKey) {
      Object.keys(this.$refs).forEach(refName => {
        if (refName !== filterKey) {
          this.$refs[refName].hideChooser()
        }
      })
    },
    shouldShowFilter(key) {
      return this.allowedFilterKeys.includes(key)
    },

    updateKey(key, {value}) {
      this.$emit('change', {key, value})
    }
  }
}
</script>

<style lang="scss" scoped>
  .advanced-filters-section {
    padding: 15px 20px 3px 0;
  }

  .toggle-button {
    font-size: 12px;
  }
</style>
